import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { classNames } from "../../helpers/classNames";
import Button from "../form/Button";
import React, { cloneElement, useEffect, useRef, useState } from "react";
import { elementResize } from "../../helpers";

function isReactElement(value) {
  return React.isValidElement(value);
}

export default function Drawer({
  actualSize,
  size = "xl4",
  open = false,
  onClose = () => { },
  header,
  title = "Modal title",
  subtitle = "Modal subtitle",
  children,
  animationDuration = 0.2,
  headerClass,
  openFrom = "right",
  drawerBodyClass,
  closeBtn = true,
  closeBtnProps = {},
}) {
  const [sizeValue, setSizeValue] = useState(size);

  useEffect(() => {
    setSizeValue(size);
  }, [size]);

  useEffect(() => {
    if (open) {
      document.documentElement.classList.add("!overflow-hidden");
    } else {
      document.documentElement.classList.remove("!overflow-hidden");
    }

    return () => {
      document.documentElement.classList.remove("!overflow-hidden");
    };
  }, [open]);


  const drawerSize = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    xl2: "max-w-2xl",
    xl3: "max-w-3xl",
    xl4: "max-w-4xl",
    xl5: "max-w-5xl",
    xl6: "max-w-6xl",
    xl7: "max-w-7xl",
    xl8: "max-w-8xl",
    xl9: "max-w-9xl",
    full: "max-w-full !rounded-none",
  };

  // Enhanced motion configurations with better easing and spring physics
  const getMotionProps = () => {
    const springConfig = {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8,
    };

    const easeConfig = {
      type: "tween",
      duration: animationDuration,
      ease: [0.25, 0.46, 0.45, 0.94], // Custom cubic-bezier for smooth feel
    };

    if (sizeValue === "full") {
      return {
        initial: { opacity: 0, scale: 0.9, y: 20 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.9, y: 20 },
        transition: springConfig,
      };
    }

    switch (openFrom) {
      case "left":
        return {
          initial: { x: "-100%", opacity: 0 },
          animate: { x: 0, opacity: 1 },
          exit: { x: "-100%", opacity: 0 },
          transition: easeConfig,
        };
      case "top":
        return {
          initial: { y: "-100%", opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { y: "-100%", opacity: 0 },
          transition: easeConfig,
        };
      case "bottom":
        return {
          initial: { y: "100%", opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { y: "100%", opacity: 0 },
          transition: easeConfig,
        };
      case "right":
      default:
        return {
          initial: { x: "100%", opacity: 0 },
          animate: { x: 0, opacity: 1 },
          exit: { x: "100%", opacity: 0 },
          transition: easeConfig,
        };
    }
  };

  // Enhanced backdrop animation
  const getBackdropProps = () => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: {
      type: "tween",
      duration: animationDuration * 0.8,
      ease: "easeOut",
    },
  });

  const positionClasses = () => {
    if (sizeValue === "full") return "justify-center items-center";

    switch (openFrom) {
      case "left":
        return "justify-start";
      case "right":
        return "justify-end";
      case "top":
        return "items-start";
      case "bottom":
        return "items-end";
      default:
        return "justify-end";
    }
  };

  const isHorizontal = openFrom === "left" || openFrom === "right";

  const renderChildrenWithProps = () =>
    React.Children.map(children, (child) =>
      isReactElement(child) && typeof child.type !== "string"
        ? cloneElement(child, {
          actualSize,
          sizeValue,
          open,
          onClose,
          setSizeValue,
          header,
          heading: title,
          subtitle,
          children,
          animationDuration,
          headerClass,
          openFrom,
          drawerBodyClass,
          closeBtn,
        })
        : child
    );

  const renderHeaderWithProps = () =>
    React.Children.map(isReactElement(header) ? header : <></>, (child) =>
      isReactElement(child) && typeof child.type !== "string"
        ? cloneElement(child, {
          actualSize,
          sizeValue,
          open,
          onClose,
          setSizeValue,
          header,
          heading: title,
          subtitle,
          children,
          animationDuration,
          headerClass,
          openFrom,
          drawerBodyClass,
          closeBtn,
        })
        : child
    );

  return (
    <AnimatePresence mode="wait">
      {open && (
        <Dialog
          open={open}
          as="div"
          className={classNames(
            "relative",
            sizeValue === "full" || openFrom === "top" || openFrom === "bottom"
              ? "z-90"
              : "z-50"
          )}
          onClose={onClose}
        >
          {/* Enhanced backdrop with better animation */}
          <motion.div
            {...getBackdropProps()}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            aria-hidden="true"
          />

          <div className={classNames("fixed inset-0 flex", positionClasses())}>
            <motion.div
              {...getMotionProps()}
              className={classNames(
                "bg-white shadow-2xl overflow-hidden",
                // Enhanced shadow and styling
                "drop-shadow-2xl",
                isHorizontal ? "h-full w-full" : "w-full",
                // Enhanced border radius based on direction
                openFrom === "left" && "rounded-none sm:rounded-r-xl",
                openFrom === "right" && "rounded-none sm:rounded-l-xl",
                openFrom === "top" && "rounded-none sm:rounded-b-xl",
                openFrom === "bottom" && "rounded-none sm:rounded-t-xl",
                sizeValue === "full" && "rounded-none sm:rounded-xl",
                // Size classes
                openFrom === "top" || openFrom === "bottom"
                  ? drawerSize["full"]
                  : drawerSize[sizeValue]
              )}
            >
              <DialogPanel className="h-full w-full relative">
                {/* Content container with staggered animation */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{
                    delay: animationDuration * 0.3,
                    duration: animationDuration * 0.7,
                    ease: "easeOut",
                  }}
                  className="w-full h-full flex flex-col"
                >
                  {header !== false && (
                    <div className="relative flex-grow-0">
                      {header ? (
                        renderHeaderWithProps()
                      ) : (
                        <div className="p-6 py-4 bg-gray-50/80 backdrop-blur-sm border-b border-gray-200">
                          <div className={classNames("space-y-1", headerClass)}>
                            {title && (
                              <DialogTitle className="font-bold text-xl text-gray-900">
                                {title}
                              </DialogTitle>
                            )}
                            {subtitle && (
                              <Description className="text-sm text-gray-500">
                                {subtitle}
                              </Description>
                            )}
                          </div>
                        </div>
                      )}
                      {closeBtn && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{
                            delay: animationDuration * 0.4,
                            duration: animationDuration * 0.6,
                            ease: "easeOut",
                          }}
                          className="absolute top-3 right-3"
                        >
                          <Button
                            buttonLabelClasses={closeBtnProps.buttonLabelClasses || ""}
                            buttonType={closeBtnProps.buttonType || "button"}
                            buttonIcon={closeBtnProps.buttonIcon || "fa-solid fa-xmark"}
                            buttonIconPosition={closeBtnProps.buttonIconPosition || "left"}
                            buttonLabel={closeBtnProps.buttonLabel || ""}
                            buttonClasses={
                              closeBtnProps.buttonClasses ||
                              "!bg-gray-100 !text-denim !border-gray-200 hover:!bg-denim/20 !rounded-lg p-2 h-8 w-8 !min-w-8 flex items-center justify-center transition-colors duration-150"
                            }
                            buttonFunction={onClose}
                            buttonHasLink={closeBtnProps.buttonHasLink || false}
                            {...closeBtnProps}
                          />
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Body content with smooth scrolling */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{
                      delay: animationDuration * 0.4,
                      duration: animationDuration * 0.6,
                      ease: "easeOut",
                    }}
                    className={classNames(
                      "flex-grow w-full p-6 overflow-y-auto",
                      "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100",
                      header ? "max-h-[calc(100%-85px)]" : "max-h-full",
                      drawerBodyClass
                    )}
                  >
                    {renderChildrenWithProps()}
                  </motion.div>
                </motion.div>
              </DialogPanel>
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}